/* global window, console */

import { measureRichInlineStats, prepareRichInline } from "@chenglou/pretext/rich-inline";
import { setCellOverflow } from "../../state/appStateSlice";
import { getWeights } from "../../helpers/grid";
import { resize } from "../../state/structureSlice";
import { incrementGlobalFontSize } from "../../state/cellStylesSlice";
import { getIconSize } from "../../helpers/iconUtils";
import { getDPIRatio, countMinimumLines, countParagraphBlocks, extractLineSegments } from "./useCellStyleCalcs";
import { batchGroupBy } from "../../helpers/batchGroupBy";

export const MIN_WIDTH_SEARCH_MAX_PX = 10000;
export const AUTOFIT_MIN_WIDTH_PX = 30;
export const AUTOFIT_MIN_HEIGHT_PX = 30;
export const AUTOFIX_EXTRA_MARGIN = 2;
export const AUTOFIT_MIN_FONT_SIZE_PT = 6;

// Given a candidate cell width, compute the available text container width
// accounting for shape margin → shape inner inset → cell padding → icon (in order).
export function getTextContainerWidth(
  cellWidth,
  { shapeMarginH, shapeMarginHPerc, shapeInnerHPerc, paddingH, paddingHPerc, iconTotalWidth }
) {
  const afterShapeMargin = cellWidth * (1 - shapeMarginHPerc / 100) - shapeMarginH;
  const afterInnerInset = afterShapeMargin * (1 - shapeInnerHPerc / 100);
  return Math.max(0, afterInnerInset * (1 - paddingHPerc / 100) - paddingH - iconTotalWidth);
}

// Inverse of getTextContainerWidth: given a text line width, return the minimum cell width
// that accommodates it (i.e. the content width = longest line + all padding/margin back-projected).
export function getMinCellWidthForTextWidth(
  textWidth,
  { shapeMarginH, shapeMarginHPerc, shapeInnerHPerc, paddingH, paddingHPerc, iconTotalWidth }
) {
  const afterInnerInset = (textWidth + paddingH + iconTotalWidth) / (1 - paddingHPerc / 100);
  const afterShapeMargin = afterInnerInset / (1 - shapeInnerHPerc / 100);
  return (afterShapeMargin + shapeMarginH) / (1 - shapeMarginHPerc / 100);
}

export function computeMinWidth(
  preparedSegments,
  lineCount,
  lineHeightPx,
  minimumCellHeight,
  containerHeight,
  containerParams
) {
  if (containerHeight < minimumCellHeight) return -1;
  if (preparedSegments.length === 0) return AUTOFIT_MIN_WIDTH_PX;
  // Count total words. Measuring at width=1 forces each break:'never' word onto its own line,
  // so lineCount == word count.
  let wordCount = 0;
  for (const { prepared } of preparedSegments) {
    if (!prepared) {
      wordCount++;
      continue;
    }
    wordCount += measureRichInlineStats(prepared, 1).lineCount;
  }

  // ≤3 words: require single-line per segment; >3 words: allow wrapping to fill available height
  const maxLines =
    wordCount <= 3 ? lineCount : lineCount + Math.floor((containerHeight - minimumCellHeight) / lineHeightPx);

  // Binary search in cell-width space; each iteration derives text container width dynamically
  // so percentage-based margins/paddings are evaluated against the candidate width, not the current one.
  let lo = 1;
  let hi = MIN_WIDTH_SEARCH_MAX_PX;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const textContainerWidth = getTextContainerWidth(mid, containerParams);
    const totalLines = preparedSegments.reduce((sum, { prepared, indent }) => {
      if (!prepared) return sum + 1;
      return sum + measureRichInlineStats(prepared, Math.max(1, textContainerWidth - indent)).lineCount;
    }, 0);
    if (totalLines <= maxLines) hi = mid;
    else lo = mid + 1;
  }

  // Also find the min cell width where the widest atomic word fits without wrapping.
  const maxWordTextWidth = preparedSegments.reduce((max, { prepared, indent }) => {
    if (!prepared) return max;
    return Math.max(max, measureRichInlineStats(prepared, 1).maxLineWidth + indent);
  }, 0);
  let wlo = 1;
  let whi = MIN_WIDTH_SEARCH_MAX_PX;
  while (wlo < whi) {
    const wmid = (wlo + whi) >> 1;
    if (getTextContainerWidth(wmid, containerParams) >= maxWordTextWidth) whi = wmid;
    else wlo = wmid + 1;
  }

  return getMinCellWidthForTextWidth(Math.max(AUTOFIT_MIN_WIDTH_PX, Math.max(lo, wlo)), containerParams);
}

export function measureAndDispatchOverflow(el, calcs, id, dispatch) {
  const minimumWidth = computeMinWidth(
    calcs.preparedSegments,
    calcs.lineCount,
    calcs.lineHeightPx,
    calcs.minimumCellHeight,
    el.clientHeight,
    calcs.containerParams
  );
  const measuredLines = calcs.preparedSegments.reduce((sum, { prepared, indent }) => {
    if (!prepared) return sum + 1;
    return sum + measureRichInlineStats(prepared, Math.max(1, el.clientWidth - indent)).lineCount;
  }, 0);
  const textContentHeight = measuredLines * calcs.lineHeightPx + calcs.blockCount * calcs.paragraphSpacingPx;
  // When the icon has vertical impact (centered layout, gridTemplateRows:"1fr 1fr"), the text
  // occupies only the bottom half of the available area while the icon takes the top half.
  // The minimum cell height must satisfy both halves: text needs H*scaleV/2-(offsetV+paddingV)/2
  // >= textContentHeight, and the icon row must fit iconTotalHeight.
  // Derived inverses (see useCellStyleCalcs):
  //   tNeed = 2*textContentHeight + paddingV  →  H = (tNeed + offsetV) / scaleV
  //   iNeed = 2*iconTotalHeight*(1-paddingVPerc/100) - paddingV  →  H = (iNeed + offsetV) / scaleV
  const effectiveContentNeed = calcs.iconHasVerticalImpact
    ? Math.max(
        2 * textContentHeight + calcs.paddingV,
        2 * calcs.iconTotalHeight * (1 - calcs.paddingVPerc / 100) - calcs.paddingV
      )
    : textContentHeight;
  const contentHeight = Math.max(AUTOFIT_MIN_HEIGHT_PX, (effectiveContentNeed + calcs.offsetV) / calcs.scaleV);
  const maxTextLineWidth = calcs.preparedSegments.reduce((max, { prepared, indent }) => {
    if (!prepared) return max;
    return Math.max(max, measureRichInlineStats(prepared, Math.max(1, el.clientWidth - indent)).maxLineWidth + indent);
  }, 0);
  const contentWidth = Math.max(
    AUTOFIT_MIN_WIDTH_PX,
    getMinCellWidthForTextWidth(maxTextLineWidth, calcs.containerParams)
  );
  dispatch(setCellOverflow({ id, minimumWidth, contentHeight, contentWidth }));
}

// Compute the shape/padding/icon geometry for a cell style — the non-font-dependent part of
// useCellStyleCalcs. Returns the same container-geometry fields used by measureAndDispatchOverflow.
export function computeContainerGeometry(currentStyle, currentStyleSheet) {
  const defaultPadding = parseInt(currentStyleSheet?.cellPadding ?? "0");
  const paddingLeft = currentStyle?.paddingLeft ?? currentStyle?.padding ?? defaultPadding;
  const paddingLeftPerc = currentStyle?.paddingLeftPerc ?? currentStyle?.paddingPerc ?? 0;
  const paddingRight = currentStyle?.paddingRight ?? currentStyle?.padding ?? defaultPadding;
  const paddingRightPerc = currentStyle?.paddingRightPerc ?? currentStyle?.paddingPerc ?? 0;
  const paddingTop = currentStyle?.paddingTop ?? currentStyle?.padding ?? defaultPadding;
  const paddingTopPerc = currentStyle?.paddingTopPerc ?? currentStyle?.paddingPerc ?? 0;
  const paddingBottom = currentStyle?.paddingBottom ?? currentStyle?.padding ?? defaultPadding;
  const paddingBottomPerc = currentStyle?.paddingBottomPerc ?? currentStyle?.paddingPerc ?? 0;
  const paddingH = paddingLeft + paddingRight;
  const paddingHPerc = paddingLeftPerc + paddingRightPerc;
  const paddingV = paddingTop + paddingBottom;
  const paddingVPerc = paddingTopPerc + paddingBottomPerc;

  const shape = currentStyle?.shape;
  const shapeMarginLeft = shape?.marginLeft ?? shape?.margin ?? 0;
  const shapeMarginRight = shape?.marginRight ?? shape?.margin ?? 0;
  const shapeMarginTop = shape?.marginTop ?? shape?.margin ?? 0;
  const shapeMarginBottom = shape?.marginBottom ?? shape?.margin ?? 0;
  const shapeMarginLeftPerc = shape?.marginLeftPerc ?? shape?.marginPerc ?? 0;
  const shapeMarginRightPerc = shape?.marginRightPerc ?? shape?.marginPerc ?? 0;
  const shapeMarginTopPerc = shape?.marginTopPerc ?? shape?.marginPerc ?? 0;
  const shapeMarginBottomPerc = shape?.marginBottomPerc ?? shape?.marginPerc ?? 0;
  const shapeInnerHPerc = shape?.type === "Circle" ? 30 : shape?.type === "ArrowFull" ? 15 : 0;
  const shapeInnerVPerc = shape?.type === "Circle" ? 30 : 0;
  const shapeMarginH = shapeMarginLeft + shapeMarginRight;
  const shapeMarginHPerc = shapeMarginLeftPerc + shapeMarginRightPerc;
  const shapeMarginV = shapeMarginTop + shapeMarginBottom;
  const shapeMarginVPerc = shapeMarginTopPerc + shapeMarginBottomPerc;

  const iconHasHorizontalImpact =
    !!currentStyle?.icon && currentStyle?.iconAlign !== "center" && !!currentStyle?.iconAlign;
  const iconTotalWidth = (() => {
    if (!iconHasHorizontalImpact) return 0;
    const iSize = getIconSize(currentStyle.iconSize);
    const iMarginLeft = currentStyle.iconStyle?.marginLeft ?? currentStyle.iconStyle?.margin ?? 0;
    const iMarginRight = currentStyle.iconStyle?.marginRight ?? currentStyle.iconStyle?.margin ?? 0;
    const iEnclosurePadding =
      currentStyle.iconEnclosure && currentStyle.iconEnclosure !== "none" ? Math.max(iSize * 0.1 + 4, 4) : 0;
    return iSize + iMarginLeft + iMarginRight + 2 * iEnclosurePadding;
  })();
  const iconHasVerticalImpact =
    !!currentStyle?.icon && (currentStyle?.iconAlign === "center" || !currentStyle?.iconAlign);
  const iconTotalHeight = (() => {
    if (!iconHasVerticalImpact) return 0;
    const iSize = getIconSize(currentStyle.iconSize);
    const iMarginTop = currentStyle.iconStyle?.marginTop ?? currentStyle.iconStyle?.margin ?? 0;
    const iMarginBottom = currentStyle.iconStyle?.marginBottom ?? currentStyle.iconStyle?.margin ?? 0;
    const iEnclosurePadding =
      currentStyle.iconEnclosure && currentStyle.iconEnclosure !== "none" ? Math.max(iSize * 0.1 + 4, 4) : 0;
    return iSize + iMarginTop + iMarginBottom + 2 * iEnclosurePadding;
  })();

  const scaleV = (1 - shapeMarginVPerc / 100) * (1 - shapeInnerVPerc / 100) * (1 - paddingVPerc / 100);
  const offsetV = shapeMarginV * (1 - shapeInnerVPerc / 100) * (1 - paddingVPerc / 100) + paddingV;
  const containerParams = { shapeMarginH, shapeMarginHPerc, shapeInnerHPerc, paddingH, paddingHPerc, iconTotalWidth };

  return { containerParams, scaleV, offsetV, paddingV, paddingVPerc, iconHasVerticalImpact, iconTotalHeight };
}

// Headless equivalent of measureAndDispatchOverflow: compute cell overflow metrics for a given
// font size and physical cell dimensions without the DOM or any Redux dispatch.
// containerWidthPx / containerHeightPx are the CELL pixel dimensions (not the inner text container).
export function computeVirtualCellOverflow(
  json,
  fontSizePt,
  currentStyle,
  currentStyleSheet,
  containerWidthPx,
  containerHeightPx
) {
  const geometry = computeContainerGeometry(currentStyle, currentStyleSheet);
  const { containerParams, scaleV, offsetV, paddingV, paddingVPerc, iconHasVerticalImpact, iconTotalHeight } = geometry;

  const textContainerWidth = getTextContainerWidth(containerWidthPx, containerParams);
  const textContainerHeight = containerHeightPx * scaleV - offsetV;

  const sizeKey = fontSizePt + "pt";
  const defaultFont = {
    size: sizeKey,
    family: currentStyle?.fontFamily ?? "Arial",
    weight: currentStyle?.weight === "bold" ? "bold" : "normal",
    style: currentStyle?.style === "italic" ? "italic" : "normal",
  };

  const lineSpacingEntry = currentStyleSheet?.lineSpacing?.find((ls) => ls.id === sizeKey);
  const lineHeight =
    lineSpacingEntry?.line ??
    (currentStyleSheet?.lineSpacing?.length > 0
      ? currentStyleSheet.lineSpacing[fontSizePt < 8 ? 0 : currentStyleSheet.lineSpacing.length - 1].line
      : 1.3);
  const paragraphSpacing = lineSpacingEntry?.paragraph ?? 0;

  const fontSizePx = fontSizePt * getDPIRatio() * (4 / 3);
  const lineHeightPx = fontSizePx * lineHeight;
  const paragraphSpacingPx = paragraphSpacing * getDPIRatio() * (4 / 3);
  const lineCount = countMinimumLines(json);
  const blockCount = countParagraphBlocks(json);
  const minimumCellHeight = lineCount * lineHeightPx + blockCount * paragraphSpacingPx;

  const preparedSegments = extractLineSegments(json ?? null, defaultFont).map(({ items, indent }) => ({
    prepared: items.length > 0 ? prepareRichInline(items) : null,
    indent,
  }));

  const minimumWidth = computeMinWidth(
    preparedSegments,
    lineCount,
    lineHeightPx,
    minimumCellHeight,
    textContainerHeight,
    containerParams
  );

  const measuredLines = preparedSegments.reduce((sum, { prepared, indent }) => {
    if (!prepared) return sum + 1;
    return sum + measureRichInlineStats(prepared, Math.max(1, textContainerWidth - indent)).lineCount;
  }, 0);
  const textContentHeight = measuredLines * lineHeightPx + blockCount * paragraphSpacingPx;
  const effectiveContentNeed = iconHasVerticalImpact
    ? Math.max(2 * textContentHeight + paddingV, 2 * iconTotalHeight * (1 - paddingVPerc / 100) - paddingV)
    : textContentHeight;
  const contentHeight = Math.max(AUTOFIT_MIN_HEIGHT_PX, (effectiveContentNeed + offsetV) / scaleV);

  const maxTextLineWidth = preparedSegments.reduce((max, { prepared, indent }) => {
    if (!prepared) return max;
    return Math.max(
      max,
      measureRichInlineStats(prepared, Math.max(1, textContainerWidth - indent)).maxLineWidth + indent
    );
  }, 0);
  const contentWidth = Math.max(AUTOFIT_MIN_WIDTH_PX, getMinCellWidthForTextWidth(maxTextLineWidth, containerParams));

  return { minimumWidth: minimumWidth > 0 ? minimumWidth : 0, contentHeight, contentWidth };
}

// Compute the target pixel size for axis element i (max content across cells + AUTOFIX_EXTRA_MARGIN×2).
// Returns 0 if the element is a spacer, has no measurable cells, or has no overflow data.
export function computeAxisNeededSizePx(i, vertical, { cells, merged, cellOverflow, cols, rows }) {
  const axisArr = vertical ? cols : rows;
  if (axisArr[i]?.[0] === "s") return 0;

  const cellsInAxis = vertical
    ? cells.flatMap((row, ri) => (row[i] ? [{ cell: row[i], crossIndex: ri }] : []))
    : (cells[i] ?? []).map((cell, ci) => (cell ? { cell, crossIndex: ci } : null)).filter(Boolean);

  const extractSize = vertical
    ? (cell) => Math.max(cellOverflow[cell.id]?.minimumWidth ?? 0, cellOverflow[cell.id]?.contentWidth ?? 0)
    : (cell) => cellOverflow[cell.id]?.contentHeight ?? 0;

  const checkPerpMerge = vertical
    ? ({ crossIndex }) =>
        merged.some(
          (m) =>
            m.dir === "right" && m.length >= 1 && m.cell.x === crossIndex && i >= m.cell.y && i < m.cell.y + m.length
        )
    : ({ crossIndex }) =>
        merged.some(
          (m) =>
            m.dir === "down" && m.length >= 1 && m.cell.y === crossIndex && i >= m.cell.x && i < m.cell.x + m.length
        );

  const rawMax = cellsInAxis.reduce((max, entry) => {
    if (checkPerpMerge(entry)) return max;
    const value = extractSize(entry.cell);
    return value > 0 ? Math.max(max, value) : max;
  }, 0);

  if (rawMax <= 0) return 0;
  const minSize = vertical ? AUTOFIT_MIN_WIDTH_PX : AUTOFIT_MIN_HEIGHT_PX;
  return Math.max(minSize, rawMax) + AUTOFIX_EXTRA_MARGIN * 2;
}

// Like computeAxisNeededSizePx but uses headless virtual measurement at a given font decrement
// instead of the stored cellOverflow. fontDecrementPt is subtracted from each cell's current
// stored font size to simulate a smaller global font. localColWeights/localRowWeights are the
// weight arrays AFTER any previous resize dispatches (so container widths/heights are accurate).
export function computeAxisNeededSizePxVirtual(
  i,
  vertical,
  { cells, merged, cols, rows },
  fontDecrementPt,
  localRowWeights,
  rowUnit,
  localColWeights,
  colUnit,
  cellStyles,
  styleSheet
) {
  const axisArr = vertical ? cols : rows;
  if (axisArr[i]?.[0] === "s") return 0;

  // cells[rowIdx][colIdx]; crossIndex is the index on the perpendicular axis
  const cellsInAxis = vertical
    ? cells.flatMap((row, ri) => (row[i] ? [{ cell: row[i], crossIndex: ri }] : []))
    : (cells[i] ?? []).map((cell, ci) => (cell ? { cell, crossIndex: ci } : null)).filter(Boolean);

  const checkPerpMerge = vertical
    ? ({ crossIndex }) =>
        merged.some(
          (m) =>
            m.dir === "right" && m.length >= 1 && m.cell.x === crossIndex && i >= m.cell.y && i < m.cell.y + m.length
        )
    : ({ crossIndex }) =>
        merged.some(
          (m) =>
            m.dir === "down" && m.length >= 1 && m.cell.y === crossIndex && i >= m.cell.x && i < m.cell.x + m.length
        );

  let rawMax = 0;
  for (const entry of cellsInAxis) {
    if (checkPerpMerge(entry)) continue;
    const { cell, crossIndex } = entry;
    const currentStyle = cellStyles[cell.id] ?? {};
    const baseFontSizePt = parseInt(currentStyle?.size?.replace("pt", "") ?? "12");
    const virtualFontSizePt = Math.max(AUTOFIT_MIN_FONT_SIZE_PT, baseFontSizePt - fontDecrementPt);
    // Physical cell dimensions for the virtual overflow computation
    const colWidthPx = vertical ? localColWeights[i] * colUnit : localColWeights[crossIndex] * colUnit;
    const rowHeightPx = vertical ? localRowWeights[crossIndex] * rowUnit : localRowWeights[i] * rowUnit;
    const overflow = computeVirtualCellOverflow(
      cell.json ?? null,
      virtualFontSizePt,
      currentStyle,
      styleSheet,
      colWidthPx,
      rowHeightPx
    );
    const value = vertical ? Math.max(overflow.minimumWidth, overflow.contentWidth) : overflow.contentHeight;
    if (value > 0) rawMax = Math.max(rawMax, value);
  }

  if (rawMax <= 0) return 0;
  const minSize = vertical ? AUTOFIT_MIN_WIDTH_PX : AUTOFIT_MIN_HEIGHT_PX;
  return Math.max(minSize, rawMax) + AUTOFIX_EXTRA_MARGIN * 2;
}

// Find the nearest non-spacer neighbor index in arr from targetIndex in the given direction.
// direction = -1 for left/top, +1 for right/bottom. Returns -1 if none found.
export function findNeighborIndex(arr, targetIndex, direction) {
  for (let i = targetIndex + direction; i >= 0 && i < arr.length; i += direction) {
    if (arr[i][0] !== "s") return i;
  }
  return -1;
}

// After all content is fitted, try to restore the original weight proportions as closely as
// possible while respecting content-size lower bounds. Cells with surplus free space are shrunk
// proportionally so that cells constrained to their content size can grow toward their original ratio.
// Spacers are excluded — their pixel widths are fixed and not redistributable.
function restoreAxisWeightRatios(origWeights, currentWeights, neededSizes, axisUnit, axisArr, vertical, dispatch) {
  const resizeArg = (i, w) => (vertical ? resize({ columnIndex: i, weight: w }) : resize({ rowIndex: i, weight: w }));

  const nonSpacerIdxs = axisArr.map((_, i) => i).filter((i) => axisArr[i]?.[0] !== "s");
  if (nonSpacerIdxs.length < 2) return;

  const origNonSpacerSum = nonSpacerIdxs.reduce((sum, i) => sum + origWeights[i], 0);
  const currentNonSpacerSum = nonSpacerIdxs.reduce((sum, i) => sum + currentWeights[i], 0);
  if (origNonSpacerSum <= 0 || currentNonSpacerSum <= 0) return;

  // Minimum weight per cell derived from content needed size (0 for cells with no content data).
  const neededWeight = (i) => (neededSizes[i] > 0 ? neededSizes[i] / axisUnit : 0);

  // Safety: if sum of all needed weights exceeds what's available, restoration is infeasible.
  const totalNeeded = nonSpacerIdxs.reduce((sum, i) => sum + neededWeight(i), 0);
  if (totalNeeded > currentNonSpacerSum) return;

  // Scale original proportions to the current total so the ratio target preserves ratios exactly.
  const rawTarget = (i) => (origWeights[i] / origNonSpacerSum) * currentNonSpacerSum;

  // Deficit: cells whose rawTarget falls below their needed size.
  // Surplus pool: the headroom above needed for cells whose rawTarget exceeds their needed size.
  let totalDeficit = 0;
  let surplusPool = 0;
  for (const i of nonSpacerIdxs) {
    const gap = rawTarget(i) - neededWeight(i);
    if (gap < 0) totalDeficit += -gap;
    else surplusPool += gap;
  }

  // If the surplus can't cover the deficit, skip — shouldn't happen when feasibility check passed
  // but guard against floating-point edge cases.
  if (surplusPool < totalDeficit) return;

  // Compute final targets: deficit cells are pinned to their needed size; surplus cells absorb
  // the deficit proportionally to their own surplus.
  const finalTargets = nonSpacerIdxs.map((i) => {
    const rt = rawTarget(i);
    const nw = neededWeight(i);
    if (rt <= nw) return { i, w: nw };
    const surplus_i = rt - nw;
    return { i, w: rt - (totalDeficit / surplusPool) * surplus_i };
  });

  const WEIGHT_THRESHOLD = 0.05;
  for (const { i, w } of finalTargets) {
    if (Math.abs(w - currentWeights[i]) > WEIGHT_THRESHOLD) {
      dispatch(resizeArg(i, w));
    }
  }
}

// Autofit all rows (top-to-bottom) then all columns (left-to-right) to their content.
// Only grows cells whose content is currently clipped. Uses a three-tier strategy to find
// the best way to absorb the required growth without clipping other cells:
//   Tier 1 — single nearest safe neighbor (has enough slack alone)
//   Tier 2 — proportional distribution across all neighbors that have positive slack
//   Tier 3 — best-effort single neighbor (most slack); dispatched immediately, added to unsolvable
//
// When unsolvable cells remain after the first pass, the algorithm tries decreasing the global
// font size by 1pt per iteration (down to AUTOFIT_MIN_FONT_SIZE_PT) and re-solving using only
// tiers 1 & 2 with virtually-computed content sizes (no re-render, pure pretext). If a full
// solution is found, the font-size change and corrective resize dispatches are committed at once.
//
// Because resizes only redistribute between non-spacer pairs, sumWeights is conserved and
// unit stays constant, so all weight arithmetic uses a single local weights array per axis.
export function runAutofitAll(store, dispatch) {
  const state = store.getState();
  const {
    gridd: {
      present: { structure },
    },
    app: { cellOverflow },
  } = state;
  const cellStyles = state.gridd.present.cellStyles;
  const autofitDebug = state.preferences.autofitDebug;
  if (!autofitDebug) batchGroupBy.start();
  const styleSheet =
    state.styleSheet?.sheets?.[state.styleSheet?.selectedSheet] ?? state.styleSheet?.sheets?.["default"] ?? null;

  // Three-tier solver for a single axis. Returns localWeights after all dispatches.
  // Tier-3 entries (unsolvable) are pushed into the provided unsolvable array.
  const solveAxis = (vertical, neededSizes, initialWeights, axisUnit, axisArr, unsolvable) => {
    const minSize = vertical ? AUTOFIT_MIN_WIDTH_PX : AUTOFIT_MIN_HEIGHT_PX;
    const localWeights = [...initialWeights];
    const resizeArg = (i, w) => (vertical ? resize({ columnIndex: i, weight: w }) : resize({ rowIndex: i, weight: w }));

    for (let i = 0; i < axisArr.length; i++) {
      if (axisArr[i]?.[0] === "s") continue;

      const currentPx = localWeights[i] * axisUnit;
      const neededPx = neededSizes[i];
      if (neededPx <= 0 || currentPx >= neededPx) continue;

      const growthNeeded = neededPx - currentPx;
      const slacks = localWeights.map((w, j) => {
        if (j === i || axisArr[j]?.[0] === "s") return -Infinity;
        return w * axisUnit - neededSizes[j];
      });

      // --- Tier 1: nearest single safe neighbor ---
      let safeIdx = -1;
      outer: for (const dir of [+1, -1]) {
        for (let j = i + dir; j >= 0 && j < axisArr.length; j += dir) {
          if (axisArr[j]?.[0] === "s") continue;
          if (slacks[j] >= growthNeeded) {
            safeIdx = j;
            break outer;
          }
        }
      }

      if (safeIdx !== -1) {
        const finalWeight = neededPx / axisUnit;
        let finalNeighborWeight = localWeights[safeIdx] + (localWeights[i] - finalWeight);
        if (finalNeighborWeight < minSize / axisUnit) finalNeighborWeight = minSize / axisUnit;
        localWeights[i] = finalWeight;
        localWeights[safeIdx] = finalNeighborWeight;
        dispatch(resizeArg(i, finalWeight));
        dispatch(resizeArg(safeIdx, finalNeighborWeight));
        continue;
      }

      // --- Tier 2: proportional distribution across all neighbors with positive slack ---
      const positiveNeighbors = slacks.map((slack, j) => ({ j, slack })).filter(({ slack }) => slack > 0);
      const totalSlack = positiveNeighbors.reduce((sum, { slack }) => sum + slack, 0);

      if (totalSlack >= growthNeeded) {
        for (const { j, slack } of positiveNeighbors) {
          localWeights[j] -= ((slack / totalSlack) * growthNeeded) / axisUnit;
          dispatch(resizeArg(j, localWeights[j]));
        }
        localWeights[i] = neededPx / axisUnit;
        dispatch(resizeArg(i, localWeights[i]));
        continue;
      }

      // --- Tier 3: best-effort single neighbor (most slack); dispatch immediately ---
      let bestBadIdx = -1;
      let bestBadSlack = -Infinity;
      for (let j = 0; j < axisArr.length; j++) {
        if (j === i || axisArr[j]?.[0] === "s") continue;
        if (slacks[j] > bestBadSlack) {
          bestBadSlack = slacks[j];
          bestBadIdx = j;
        }
      }
      if (bestBadIdx === -1) continue;

      unsolvable.push({ vertical, index: i });
      let finalWeight = neededPx / axisUnit;
      let finalNeighborWeight = localWeights[bestBadIdx] + (localWeights[i] - finalWeight);
      const neighborMinWeight = minSize / axisUnit;
      if (finalNeighborWeight < neighborMinWeight) {
        finalWeight -= neighborMinWeight - finalNeighborWeight;
        finalNeighborWeight = neighborMinWeight;
      }
      localWeights[i] = finalWeight;
      localWeights[bestBadIdx] = finalNeighborWeight;
      dispatch(resizeArg(i, finalWeight));
      dispatch(resizeArg(bestBadIdx, finalNeighborWeight));
    }

    return localWeights;
  };

  // Try to resolve unsolvable cells using tier 1 & 2 against virtual needed sizes.
  // Works on mutable copies of the weight arrays; returns { allResolved, pendingDispatches }.
  const trySolveVirtual = (
    unsolvable,
    virtualRowNeededSizes,
    virtualColNeededSizes,
    localRowWeights,
    rowUnit,
    localColWeights,
    colUnit
  ) => {
    const { columns: cols, rows } = structure;
    const virtualRowWeights = [...localRowWeights];
    const virtualColWeights = [...localColWeights];
    const pendingDispatches = [];
    let allResolved = true;

    for (const { vertical, index: i } of unsolvable) {
      const axisArr = vertical ? cols : rows;
      const virtualWeights = vertical ? virtualColWeights : virtualRowWeights;
      const neededSizes = vertical ? virtualColNeededSizes : virtualRowNeededSizes;
      const axisUnit = vertical ? colUnit : rowUnit;
      const minSize = vertical ? AUTOFIT_MIN_WIDTH_PX : AUTOFIT_MIN_HEIGHT_PX;

      const neededPx = neededSizes[i];
      const currentPx = virtualWeights[i] * axisUnit;
      if (neededPx <= 0 || currentPx >= neededPx) continue; // already fits at the smaller font

      const growthNeeded = neededPx - currentPx;
      const slacks = virtualWeights.map((w, j) => {
        if (j === i || axisArr[j]?.[0] === "s") return -Infinity;
        return w * axisUnit - neededSizes[j];
      });

      // Tier 1
      let safeIdx = -1;
      outerV: for (const dir of [+1, -1]) {
        for (let j = i + dir; j >= 0 && j < axisArr.length; j += dir) {
          if (axisArr[j]?.[0] === "s") continue;
          if (slacks[j] >= growthNeeded) {
            safeIdx = j;
            break outerV;
          }
        }
      }

      if (safeIdx !== -1) {
        const finalWeight = neededPx / axisUnit;
        let finalNeighborWeight = virtualWeights[safeIdx] + (virtualWeights[i] - finalWeight);
        if (finalNeighborWeight < minSize / axisUnit) finalNeighborWeight = minSize / axisUnit;
        virtualWeights[i] = finalWeight;
        virtualWeights[safeIdx] = finalNeighborWeight;
        pendingDispatches.push({ vertical, index: i, weight: finalWeight });
        pendingDispatches.push({ vertical, index: safeIdx, weight: finalNeighborWeight });
        continue;
      }

      // Tier 2
      const positiveNeighbors = slacks.map((slack, j) => ({ j, slack })).filter(({ slack }) => slack > 0);
      const totalSlack = positiveNeighbors.reduce((sum, { slack }) => sum + slack, 0);

      if (totalSlack >= growthNeeded) {
        for (const { j, slack } of positiveNeighbors) {
          virtualWeights[j] -= ((slack / totalSlack) * growthNeeded) / axisUnit;
          pendingDispatches.push({ vertical, index: j, weight: virtualWeights[j] });
        }
        virtualWeights[i] = neededPx / axisUnit;
        pendingDispatches.push({ vertical, index: i, weight: virtualWeights[i] });
        continue;
      }

      allResolved = false;
    }

    return { allResolved, pendingDispatches };
  };

  // --- Round 1: solve all axes using stored cellOverflow ---
  const { columns: cols, rows, cells, merged } = structure;

  const makeAxisParams = (vertical) => {
    const axisArr = vertical ? cols : rows;
    const spacerSum = axisArr.filter((a) => a[0] === "s").reduce((sum, a) => sum + parseInt(a.slice(1)), 0);
    const weights = getWeights(axisArr);
    const sumWeights = weights.reduce((sum, w, i) => (axisArr[i][0] !== "s" ? sum + w : sum), 0);
    const windowSize = vertical ? window.innerWidth : window.innerHeight;
    const unit = (windowSize - 56 - spacerSum) / sumWeights;
    const neededSizes = axisArr.map((_, i) =>
      computeAxisNeededSizePx(i, vertical, { cells, merged, cellOverflow, cols, rows })
    );
    return { axisArr, weights, unit, neededSizes };
  };

  const rowParams = makeAxisParams(false);
  const colParams = makeAxisParams(true);

  // Save original weights before any dispatches so ratio restoration can reference them.
  const origRowWeights = [...rowParams.weights];
  const origColWeights = [...colParams.weights];

  const unsolvableRows = [];
  const unsolvableCols = [];

  let localRowWeights = solveAxis(
    false,
    rowParams.neededSizes,
    rowParams.weights,
    rowParams.unit,
    rowParams.axisArr,
    unsolvableRows
  );
  let localColWeights = solveAxis(
    true,
    colParams.neededSizes,
    colParams.weights,
    colParams.unit,
    colParams.axisArr,
    unsolvableCols
  );

  // Track the effective needed sizes used to bound ratio restoration. Updated if font shrinks.
  let effectiveRowNeededSizes = rowParams.neededSizes;
  let effectiveColNeededSizes = colParams.neededSizes;

  const unsolvable = [...unsolvableRows, ...unsolvableCols];

  // --- Font-shrink loop: try decreasing global font 1pt at a time ---
  if (unsolvable.length > 0) {
    const allCellIds = cells.flatMap((row) => row.filter(Boolean).map((c) => c.id));
    const minOriginalFontSizePt = allCellIds.reduce((min, id) => {
      return Math.min(min, parseInt(cellStyles[id]?.size?.replace("pt", "") ?? "12"));
    }, Infinity);
    const maxDecrement = Math.max(0, minOriginalFontSizePt - AUTOFIT_MIN_FONT_SIZE_PT);

    let solved = false;
    for (let decrement = 0; decrement <= maxDecrement; decrement++) {
      const virtualStructure = { cells, merged, cols, rows };
      const virtualRowNeededSizes = rowParams.axisArr.map((_, i) =>
        computeAxisNeededSizePxVirtual(
          i,
          false,
          virtualStructure,
          decrement,
          localRowWeights,
          rowParams.unit,
          localColWeights,
          colParams.unit,
          cellStyles,
          styleSheet
        )
      );
      const virtualColNeededSizes = colParams.axisArr.map((_, j) =>
        computeAxisNeededSizePxVirtual(
          j,
          true,
          virtualStructure,
          decrement,
          localRowWeights,
          rowParams.unit,
          localColWeights,
          colParams.unit,
          cellStyles,
          styleSheet
        )
      );

      const { allResolved, pendingDispatches } = trySolveVirtual(
        unsolvable,
        virtualRowNeededSizes,
        virtualColNeededSizes,
        localRowWeights,
        rowParams.unit,
        localColWeights,
        colParams.unit
      );

      if (allResolved) {
        if (decrement > 0) {
          dispatch(incrementGlobalFontSize(-decrement));
        }
        for (const { vertical, index, weight } of pendingDispatches) {
          dispatch(vertical ? resize({ columnIndex: index, weight }) : resize({ rowIndex: index, weight }));
          // Keep local weight arrays in sync so ratio restoration has accurate baselines.
          if (vertical) localColWeights[index] = weight;
          else localRowWeights[index] = weight;
        }
        effectiveRowNeededSizes = virtualRowNeededSizes;
        effectiveColNeededSizes = virtualColNeededSizes;
        solved = true;
        break;
      }
    }

    if (!solved) {
      // eslint-disable-next-line no-console
      console.log("[Autofit All] Unsolvable cells:", unsolvable);
    }
  }

  // --- Ratio restoration: shrink surplus cells to recover original weight proportions ---
  restoreAxisWeightRatios(
    origRowWeights,
    localRowWeights,
    effectiveRowNeededSizes,
    rowParams.unit,
    rows,
    false,
    dispatch
  );
  restoreAxisWeightRatios(
    origColWeights,
    localColWeights,
    effectiveColNeededSizes,
    colParams.unit,
    cols,
    true,
    dispatch
  );
  if (!autofitDebug) batchGroupBy.end();
}

// Autofit a column (vertical=true) or row (vertical=false) at `index` to its content.
// preferredNeighborDirection: try this direction first for the neighbor that absorbs the delta.
export function runAutofit({
  vertical,
  index,
  cols,
  rows,
  cells,
  merged,
  cellOverflow,
  dispatch,
  preferredNeighborDirection,
}) {
  const axisArr = vertical ? cols : rows;
  if (axisArr[index]?.[0] === "s") return;

  const maxSize = computeAxisNeededSizePx(index, vertical, { cells, merged, cellOverflow, cols, rows });
  if (maxSize <= 0) return;

  const minSize = vertical ? AUTOFIT_MIN_WIDTH_PX : AUTOFIT_MIN_HEIGHT_PX;

  const spacerSum = axisArr.filter((a) => a[0] === "s").reduce((sum, a) => sum + parseInt(a.slice(1)), 0);
  const weights = getWeights(axisArr);
  const sumWeights = weights.reduce((sum, a, i) => (axisArr[i][0] !== "s" ? sum + a : sum), 0);
  const windowSize = vertical ? window.innerWidth : window.innerHeight;
  const unit = (windowSize - 56 - spacerSum) / sumWeights;
  const newWeight = maxSize / unit;
  const delta = weights[index] - newWeight;

  const fallbackDirection = preferredNeighborDirection === +1 ? -1 : +1;
  let neighborId = findNeighborIndex(axisArr, index, preferredNeighborDirection);
  if (neighborId === -1) neighborId = findNeighborIndex(axisArr, index, fallbackDirection);
  const neighborWeight = neighborId !== -1 ? weights[neighborId] : 0;

  const resizeArg = (i, w) => (vertical ? resize({ columnIndex: i, weight: w }) : resize({ rowIndex: i, weight: w }));

  let finalWeight = newWeight;
  let finalNeighborWeight = neighborId !== -1 ? neighborWeight + delta : null;

  if (finalNeighborWeight !== null) {
    const neighborMinWeight = minSize / unit;
    if (finalNeighborWeight < neighborMinWeight) {
      finalWeight -= neighborMinWeight - finalNeighborWeight;
      finalNeighborWeight = neighborMinWeight;
    }
  }

  dispatch(resizeArg(index, finalWeight));
  if (finalNeighborWeight !== null && finalNeighborWeight > 0) {
    dispatch(resizeArg(neighborId, finalNeighborWeight));
  }
}
